import { countries } from '@/common/constant';
import type { PairingFilter } from '@/common/db-typebox/pairing.typebox';
import type { WineType } from '@/common/generated/wine-product.typebox';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import {
  Button,
  Cascader,
  Form,
  InputNumber,
  Select,
  Slider,
  Switch,
} from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { RangeSlider } from './RangeSlider';

export interface Supplier {
  id: string;
  name: string;
  sources: {
    id: string;
    name: string;
  }[];
}

export interface SearchFilter extends PairingFilter {
  selectedSuppliers: string[][];
}

interface SearchFilterProps {
  suppliers: Supplier[];
  filter: SearchFilter;
  onChange: (filter: SearchFilter) => void;
}

export function SearchFilterComponent({
  suppliers,
  filter,
  onChange,
}: SearchFilterProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { symbol, priceRange } = useCurrencySymbol();
  const [form] = Form.useForm();

  const SLIDER_MIN_SCORE = 0;
  const SLIDER_MAX_SCORE = 100;
  const CURRENT_YEAR = new Date().getFullYear();
  const SLIDER_MIN_YEAR = 1900;
  const SLIDER_MAX_YEAR = CURRENT_YEAR;
  const SLIDER_MIN_ALCOHOL = 0;
  const SLIDER_MAX_ALCOHOL = 30;

  const cascaderOptions = useMemo(() => {
    return suppliers.map((supplier) => ({
      label: supplier.name,
      value: `supplier:${supplier.id}`,
      children: supplier.sources.map((source) => ({
        label: source.name,
        value: `source:${source.id}`,
      })),
    }));
  }, [suppliers]);

  const handleSelectAllSuppliers = () => {
    const allSupplierPaths = suppliers.map((supplier) => [
      `supplier:${supplier.id}`,
    ]);
    form.setFieldValue('selectedSuppliers', allSupplierPaths);
    onChange({ ...filter, selectedSuppliers: allSupplierPaths });
  };

  const handleFormValuesChange = (_: any, allValues: SearchFilter) => {
    onChange(allValues);
  };

  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      label: country,
      value: country,
    }));
  }, []);

  return (
    <Form
      form={form}
      initialValues={filter}
      onValuesChange={handleFormValuesChange}
      className="space-y-2"
    >
      <div className="flex gap-2">
        <Form.Item name="selectedSuppliers" className="flex-1 mb-0">
          <Cascader
            style={{ width: '100%' }}
            options={cascaderOptions}
            multiple
            maxTagCount="responsive"
            placeholder="Select suppliers and sources"
            showSearch
            changeOnSelect={false}
          />
        </Form.Item>
        <Button onClick={handleSelectAllSuppliers}>Select All Suppliers</Button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="text"
          size="small"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="px-0 text-gray-500 hover:text-gray-700"
        >
          {showAdvanced ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
        </Button>
      </div>

      <AnimatePresence>
        {showAdvanced && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 rounded-md border border-gray-100 bg-gray-50/50 p-3">
              <Form.Item
                name="allow_null_reviews"
                label="Include unreviewed wines"
                valuePropName="checked"
                className="mb-0"
              >
                <Switch size="small" />
              </Form.Item>

              <div className="space-y-3">
                <Form.Item
                  name="wine_types"
                  label="Wine Type:"
                  className="flex-1 mb-0"
                >
                  <div className="flex gap-2">
                    <Select
                      mode="multiple"
                      placeholder="Select wine types"
                      options={[
                        { label: 'Red', value: 'red' },
                        { label: 'White', value: 'white' },
                        { label: 'Sparkling', value: 'sparkling' },
                        { label: 'Rosé', value: 'rosé' },
                        { label: 'Dessert', value: 'dessert' },
                        { label: 'Sherry', value: 'sherry' },
                        { label: 'Port', value: 'port' },
                        { label: 'Orange', value: 'orange' },
                        { label: 'Other', value: 'other' },
                      ]}
                      maxTagCount="responsive"
                      allowClear
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        type={
                          form.getFieldValue('wine_types')?.length === 8 &&
                          form.getFieldValue('wine_types')?.includes('red') &&
                          form.getFieldValue('wine_types')?.includes('white') &&
                          form
                            .getFieldValue('wine_types')
                            ?.includes('sparkling') &&
                          form.getFieldValue('wine_types')?.includes('rosé') &&
                          form
                            .getFieldValue('wine_types')
                            ?.includes('dessert') &&
                          form
                            .getFieldValue('wine_types')
                            ?.includes('sherry') &&
                          form.getFieldValue('wine_types')?.includes('port') &&
                          form.getFieldValue('wine_types')?.includes('orange')
                            ? 'primary'
                            : 'default'
                        }
                        onClick={() => {
                          const allWineTypes: WineType[] = [
                            'red',
                            'white',
                            'sparkling',
                            'rosé',
                            'dessert',
                            'sherry',
                            'port',
                            'orange',
                          ];
                          form.setFieldValue('wine_types', allWineTypes);
                          onChange({ ...filter, wine_types: allWineTypes });
                        }}
                      >
                        Only Wine
                      </Button>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  name="limit"
                  label="Wine result amount limit:"
                  className="mb-0"
                >
                  <InputNumber
                    className="w-24"
                    placeholder="limit wine"
                    precision={0}
                    min={0}
                    step={1}
                  />
                </Form.Item>

                <Form.Item
                  name="price_range"
                  label="Price Range:"
                  className="flex-1 mb-0"
                >
                  <RangeSlider
                    name="price_range"
                    min={priceRange.min}
                    max={priceRange.max}
                    step={10}
                    prefix={symbol}
                  />
                </Form.Item>

                <Form.Item
                  name="include_no_price"
                  label="Include wines with no price"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Switch
                    checkedChildren="Include No Price"
                    unCheckedChildren="Exclude No Price"
                  />
                </Form.Item>

                <div className="flex items-start gap-4">
                  <Form.Item
                    name="min_score"
                    label="Minimum Score:"
                    className="flex-1 mb-0"
                    getValueFromEvent={(value: number) =>
                      value === SLIDER_MIN_SCORE ? undefined : value
                    }
                    getValueProps={(value: number | undefined) => ({
                      value: value ?? SLIDER_MIN_SCORE,
                    })}
                  >
                    <Slider
                      min={SLIDER_MIN_SCORE}
                      max={SLIDER_MAX_SCORE}
                      step={1}
                      marks={{
                        [SLIDER_MIN_SCORE]: 'Any',
                        [SLIDER_MAX_SCORE]: '100',
                      }}
                      tooltip={{
                        formatter: (value) => {
                          if (value === 0) return 'Any';
                          return `${value}`;
                        },
                      }}
                      className={
                        !form.getFieldValue('min_score') ? 'opacity-50' : ''
                      }
                    />
                  </Form.Item>
                  <div className="pt-[7px] text-sm text-blue-400 font-bold">
                    {form.getFieldValue('min_score') == null
                      ? 'Any'
                      : form.getFieldValue('min_score')}
                  </div>
                </div>

                <Form.Item
                  name="year"
                  label="Vintage Year:"
                  className="flex-1 mb-0"
                >
                  <RangeSlider
                    name="year"
                    min={SLIDER_MIN_YEAR}
                    max={SLIDER_MAX_YEAR}
                    step={1}
                  />
                </Form.Item>

                <Form.Item
                  name="alcohol_range"
                  label="Alcohol %:"
                  className="flex-1 mb-0"
                >
                  <RangeSlider
                    name="alcohol_range"
                    min={SLIDER_MIN_ALCOHOL}
                    max={SLIDER_MAX_ALCOHOL}
                    step={0.5}
                    suffix="%"
                    formatter={(value) => (value ? `${value}%` : '')}
                    parser={(value) =>
                      value ? Number.parseFloat(value.replace('%', '')) || 0 : 0
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="countries"
                  label="Countries:"
                  className="flex-1 mb-0"
                >
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select countries"
                    options={countryOptions}
                    maxTagCount="responsive"
                    showSearch
                    allowClear
                  />
                </Form.Item>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Form>
  );
}
